import { BaseApiService } from '@/shared/api/services/base.service';
import { buildUrl } from '@/shared/lib/query-params';
import type {
  PlanResource,
  PlansResponse,
  PlanResponse,
  SubscriptionResource,
  SubscriptionsResponse,
  SubscriptionResponse,
} from '@/types/subscription';

class SubscriptionService extends BaseApiService {
  async getPublicPlans(): Promise<PlanResource[]> {
    const response = await this.httpGet<PlansResponse>('/v1/plans/public');
    return response.data;
  }

  async getPlans(params?: {
    sort?: string;
    filter?: Record<string, string | number | boolean>;
  }): Promise<PlanResource[]> {
    const url = buildUrl('/v1/plans', {
      sort: params?.sort,
      filter: params?.filter,
    });
    const response = await this.httpGet<PlansResponse>(url);
    return response.data;
  }

  async getTrialPlans(params?: {
    sort?: string;
    filter?: Record<string, string | number | boolean>;
  }): Promise<PlanResource[]> {
    const trialFilter = {
      is_trial: true,
      is_active: true,
      is_public: true,
      ...params?.filter,
    };
    return this.getPlans({
      sort: params?.sort || 'price',
      filter: trialFilter,
    });
  }

  async getPlan(planId: string): Promise<PlanResource> {
    const response = await this.httpGet<PlanResponse>(`/v1/plans/${planId}`);
    return response.data;
  }

  async getSubscriptions(): Promise<SubscriptionResource[]> {
    const response = await this.httpGet<SubscriptionsResponse>('/v1/subscriptions');
    return response.data;
  }

  async getSubscription(subscriptionId: string): Promise<SubscriptionResource> {
    const response = await this.httpGet<SubscriptionResponse>(
      `/v1/subscriptions/${subscriptionId}`
    );
    return response.data;
  }

  async cancelSubscription(
    subscriptionId: string,
    reason?: string
  ): Promise<void> {
    const options = reason
      ? {
          body: JSON.stringify({
            data: {
              type: 'subscription',
              id: subscriptionId,
              attributes: {
                cancel_reason: reason,
              },
            },
          }),
        }
      : undefined;

    await this.httpDelete(`/v1/subscriptions/${subscriptionId}`, options);
  }

  // NO RETRY for payment operations
  async createDirectSubscription(
    planId: string,
    promoCode?: string,
    billingPeriod?: { id: string; type: string }
  ): Promise<SubscriptionResource> {
    const response = await this.post<SubscriptionResponse>(
      '/v1/subscriptions',
      this.formatJsonApiData('subscription', {
        plan_id: planId,
        promo_code: promoCode || null,
        billing_period: billingPeriod || { id: planId, type: 'resource' },
      })
    );
    return response.data;
  }

  async comparePlans(
    fromPlanId: string,
    toPlanId: string
  ): Promise<{ gains: string[]; losses: string[] }> {
    const response = await this.httpGet<{
      data: {
        type: string;
        attributes: {
          from_plan: string;
          to_plan: string;
          gains: string[];
          losses: string[];
        };
      };
    }>(`/v1/plans/${fromPlanId}/compare/${toPlanId}`);
    return response.data.attributes;
  }

  async resume(subscriptionId: string): Promise<SubscriptionResource> {
    const response = await this.post<SubscriptionResponse>(
      `/v1/subscriptions/${subscriptionId}/resume`,
      this.formatJsonApiData('subscription', {}, undefined, subscriptionId)
    );
    return response.data;
  }
}

export const subscriptionService = new SubscriptionService();
